// Finding a phone model from what people actually type: Arabic or English names, Arabic-Indic digits,
// shorthand ("ip 17 pm", "s26u") and small typos ("iphon", "galxy").

import { westernDigits } from "./saudi";

// Arabic spellings of the words in model names, mapped to the English token in the library's names.
const AR_WORDS: [RegExp, string][] = [
  [/[آأإا]ي?فون|اي فون/g, " iphone "],
  [/جا?لا?كسي|جالاكسي|غالاكسي|جلكسي/g, " galaxy "],
  [/سامسونج|سامسونغ/g, " samsung "],
  [/ابل|أبل|آبل/g, " apple "],
  [/بكسل|بيكسل/g, " pixel "],
  [/جوجل|قوقل|غوغل/g, " google "],
  [/شاومي|شياومي/g, " xiaomi "],
  [/ريدمي/g, " redmi "],
  [/هواوي/g, " huawei "],
  [/هونر|هونور/g, " honor "],
  [/ون ?بلس/g, " oneplus "],
  [/برو ?ماكس/g, " pro max "],
  [/برو/g, " pro "],
  [/ماكس/g, " max "],
  [/بلس/g, " plus "],
  [/[اأ]لترا/g, " ultra "],
  [/ميني/g, " mini "],
  [/[اإ]يدج/g, " edge "],
  [/فولد/g, " fold "],
  [/فليب|فلب/g, " flip "],
  [/(^|\s)زد(?=\s|$)/g, " z "],
  [/نوت/g, " note "],
  [/[اإ]ير/g, " air "],
];

const TATWEEL = String.fromCharCode(0x640); // the Arabic stretching stroke, as in "ايفـــون"

/** Lower-case Latin tokens, digits split from letters ("s26u" → s 26 u, "17pm" → 17 pm). */
function tokens(input: string): string[] {
  // Arabic short vowels and tatweel don't change the word.
  let s = westernDigits(input).normalize("NFKC").toLowerCase().replace(/\p{M}/gu, "").replaceAll(TATWEEL, "");
  for (const [re, en] of AR_WORDS) s = s.replace(re, en);
  s = s.replace(/\+/g, " plus ").replace(/[^\p{L}\p{N}]+/gu, " ").replace(/(\d)(\p{L})/gu, "$1 $2").replace(/(\p{L})(\d)/gu, "$1 $2");
  return s.split(" ").filter(Boolean);
}

// Shorthand people use for versions.
const SHORT: Record<string, string[]> = { pm: ["pro", "max"], u: ["ultra"], p: ["pro"], ip: ["iphone"], gs: ["galaxy", "s"] };

function expand(t: string[]): string[] {
  return t.flatMap((x) => SHORT[x] ?? [x]);
}

/** One edit (insert, delete, substitute or swap) apart. */
function nearlyEqual(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (i === a.length || i === b.length) return true;
  const rest = (x: number, y: number) => a.slice(i + x) === b.slice(i + y);
  return rest(1, 1) || rest(1, 0) || rest(0, 1) || (a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2));
}

/** How well a query token matches a name token: exact > prefix > typo. Numbers must match exactly. */
function tokenScore(q: string, n: string): number {
  if (q === n) return 3;
  if (/^\d/.test(q) || /^\d/.test(n)) return 0;
  if (n.startsWith(q)) return 2;
  if (q.length >= 4 && (nearlyEqual(q, n) || nearlyEqual(q, n.slice(0, q.length)))) return 1;
  return 0;
}

export type Searchable = { brand: string; model: string };

/**
 * Ranks models for a query. Every query word must match some word of "brand model"; ties go to the
 * model with fewer extra words (so "iphone 17" lists iPhone 17 before iPhone 17 Pro Max).
 */
export function searchDevices<T extends Searchable>(items: T[], query: string, limit = 20): T[] {
  const q = expand(tokens(query));
  if (!q.length) return [];
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const name = tokens(`${item.brand} ${item.model}`);
    let score = 0;
    const used = new Set<number>();
    for (const qt of q) {
      let best = 0;
      let at = -1;
      name.forEach((nt, i) => {
        if (used.has(i)) return;
        const s = tokenScore(qt, nt);
        if (s > best) [best, at] = [s, i];
      });
      if (!best) {
        score = -1;
        break;
      }
      used.add(at);
      score += best;
    }
    if (score > 0) scored.push({ item, score: score * 10 - (name.length - used.size) });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map((s) => s.item);
}
